/* eslint-disable no-console */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Backdrop,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  Modal,
  Paper,
  Typography,
  IconButton,
} from '@mui/material'
import { Close } from '@mui/icons-material'

import axios from 'src/utilities/http-client'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import Survey, { SurveyFormValues } from 'src/v3/components/survey'
import {
  selectClassID,
  selectCourseByID,
  selectCourseID,
  selectCoursesStatus,
  selectCourseAccreditationsByID,
  selectCourseAccreditationsStatus,
  fetchCourses,
  fetchCourseAccreditations,
} from './classroom.slice'

export default () => {
  let navigate = useNavigate()
  let dispatch = useAppDispatch()

  let [loading, setLoading] = useState(false)
  let [showQuizModal, setShowQuizModal] = useState(false)
  let coursesStatus = useAppSelector(selectCoursesStatus)
  let courseID = useAppSelector(selectCourseID)
  let course = useAppSelector(selectCourseByID(courseID))
  let classID = useAppSelector(selectClassID)
  let courseAccreditations = useAppSelector(
    selectCourseAccreditationsByID(courseID),
  )
  let courseAccreditationsStatus = useAppSelector(
    selectCourseAccreditationsStatus,
  )

  // this is necessary to ensure correct questionnaire flow on page refresh
  useEffect(() => {
    if (coursesStatus === 'uninitialized') {
      dispatch(fetchCourses())
    }
  }, [coursesStatus])

  // Fetch accreditations to determine survey eligibility
  useEffect(() => {
    if (
      courseID &&
      (!courseAccreditationsStatus[courseID] ||
        courseAccreditationsStatus[courseID] === 'uninitialized')
    ) {
      dispatch(fetchCourseAccreditations(courseID))
    }
  }, [courseAccreditationsStatus, courseID, dispatch])

  let submitLinkedServiceCE = () => {
    setLoading(true)
    axios
      .post('/api/v2/classroom/completed-ce', { CourseID: courseID })
      .then(({ data }) => data)
      .then((data) => {
        navigate(`/classroom/certificate/${data}/${courseID}/${classID}`)
      })
      .catch((error) => {
        setLoading(false)
        if (error.response?.status === 403) {
          setShowQuizModal(true)
        } else {
          console.error('Error completing linked service course:', error)
        }
      })
  }

  let isNotAccreditedLinkedService = () =>
    !!(
      course?.linkedServiceID &&
      courseAccreditations?.includes('No Accreditation')
    )

  let handleSurveySubmit = async (values: SurveyFormValues) => {
    setLoading(true)
    return axios
      .post('/api/v3/classroom/course-feedback', {
        courseID,
        ...values,
      })
      .then(() =>
        axios.post('/api/v2/classroom/completed-ce', {
          CourseID: courseID,
        }),
      )
      .then(({ data }) => {
        navigate(`/classroom/certificate/${data}/${courseID}/${classID}`)
      })
      .catch((error: any) => {
        setLoading(false)
        if (error.response?.status === 403) {
          setShowQuizModal(true)
        } else {
          console.error('Error completing course:', error)
        }
      })
  }

  let isLoadingAccreditations =
    course?.linkedServiceID &&
    courseAccreditationsStatus[courseID] !== 'fulfilled'
  let shouldShowSurvey = !isNotAccreditedLinkedService()

  return (
    <Box sx={{ m: 4, pt: 10 }}>
      {isLoadingAccreditations ? (
        <div>Loading...</div>
      ) : shouldShowSurvey ? (
        <Survey onSubmit={handleSurveySubmit} />
      ) : (
        <>
          <Box
            sx={{
              position: 'absolute',
              opacity: 0.5,
              backgroundImage: `url(${import.meta.env.VITE_CLOUDFRONT_URL}/public/${course?.imageName})`,
              backgroundSize: 'cover',
              width: '100%',
              height: 'calc(100vh - 64px)',
            }}
          ></Box>
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
            }}
          >
            <Card
              sx={{
                mt: 10,
                py: 2,
                px: 3,
                border: '2px solid primary.dark',
                boxShadow: '5px 5px 5px background.paper',
                borderRadius: 5,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <CardContent sx={{ py: 1 }}>
                <Typography
                  variant="h4"
                  sx={{ textAlign: 'center', fontFamily: 'smileAndWave' }}
                >
                  {course?.name}
                </Typography>
                <Typography>
                  There is no survey for this course. You may now continue to
                  get your certificate.
                </Typography>
              </CardContent>
              <CardActions>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => submitLinkedServiceCE()}
                >
                  Generate Certificate
                </Button>
              </CardActions>
            </Card>
          </Box>
        </>
      )}
      <div>
        <Backdrop
          sx={{
            backdrop: {
              zIndex: 'theme.zindex.drawer',
              color: '#fff',
            },
          }}
          open={loading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </div>

      {/* Quiz Completion Required Modal */}
      <Modal
        open={showQuizModal}
        onClose={() => setShowQuizModal(false)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Paper
          sx={{
            width: { xs: '90vw', sm: '500px' },
            maxWidth: '90vw',
            p: 3,
            position: 'relative',
            bgcolor: 'background.paper',
          }}
        >
          {/* Close Button */}
          <IconButton
            onClick={() => setShowQuizModal(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <Close />
          </IconButton>

          <Typography variant="h6" sx={{ mb: 3, pr: 5 }}>
            Quiz Completion Required
          </Typography>

          <Typography variant="body1" sx={{ mb: 2 }}>
            This course can only be completed if you have completed and passed
            the quiz for the course.
          </Typography>

          <Typography variant="body2" sx={{ mb: 3 }}>
            If you believe you are getting this message in error, you may
            contact support.
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" onClick={() => setShowQuizModal(false)}>
              OK
            </Button>
          </Box>
        </Paper>
      </Modal>
    </Box>
  )
}
